import { useState, useEffect } from "react";
import axios from "axios";
import { Upload, Download, History, Clock, ChevronDown, Plus, RefreshCw, ArrowRight, AlertTriangle, FileSpreadsheet, Trash2, Settings2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPORT_COLUMNS = [
  { id: "ean", label: "EAN", default: true },
  { id: "artikelnummer", label: "Artikelnummer", default: true },
  { id: "produktnamn", label: "Beskrivning", default: true },
  { id: "lagerplats", label: "Lagerplats", default: true },
  { id: "antal", label: "Totalt antal", default: true },
  { id: "booked", label: "Bokade", default: false },
  { id: "available", label: "Tillgängliga", default: false },
  { id: "tillverkare", label: "Tillverkare", default: false },
  { id: "kategori", label: "Kategori", default: false },
  { id: "inkommet", label: "Inkommet", default: false },
  { id: "bokningsinfo", label: "Bokningsinfo", default: false },
  { id: "inkops_pris", label: "Inköpspris", default: false },
  { id: "salj_pris", label: "Säljpris", default: false },
];

export default function ImportExportPage() {
  const { isAdmin } = useAuth();
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showDetailsDialog, setShowDetailsDialog] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showCustomExportDialog, setShowCustomExportDialog] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(
    EXPORT_COLUMNS.filter(c => c.default).map(c => c.id)
  );
  const [exportMode, setExportMode] = useState("detailed"); // "detailed" or "grouped"

  const fetchTransferHistory = async () => {
    try {
      const response = await axios.get(`${API}/inventory/transfer-history?limit=50`);
      setTransferHistory(response.data);
    } catch (error) {
      console.error("Error fetching transfer history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchTransferHistory();
    }
  }, [isAdmin]);

  if (!isAdmin) {
    return <Navigate to="/inventory" replace />;
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setImporting(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("mode", "merge");

    try {
      const response = await axios.post(`${API}/inventory/import-excel`, formData);
      toast.success(response.data.message);
      setShowImportDialog(false);
      setSelectedFile(null);
      fetchTransferHistory();
    } catch (error) {
      toast.error("Fel vid import: " + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = () => {
    setSelectedFile(null);
    setShowImportDialog(false);
  };

  const handleExportSiteSmart = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/inventory/export/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `lagerexport_sitesmart_${new Date().toISOString().slice(0,10)}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export klar!");
      fetchTransferHistory();
    } catch (error) {
      toast.error("Kunde inte exportera: " + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  const handleCustomExport = async () => {
    if (selectedColumns.length === 0) {
      toast.error("Välj minst en kolumn");
      return;
    }

    setExporting(true);
    try {
      const response = await axios.get(`${API}/inventory/export/custom`, {
        params: { 
          columns: selectedColumns.join(','),
          mode: exportMode
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lagerexport_anpassad_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export klar!");
      setShowCustomExportDialog(false);
      fetchTransferHistory();
    } catch (error) {
      toast.error("Kunde inte exportera: " + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  const handleClearHistory = async () => {
    setClearing(true);
    try {
      const response = await axios.delete(`${API}/inventory/transfer-history`);
      toast.success(response.data.message || "Historik rensad");
      setShowClearDialog(false);
      fetchTransferHistory();
    } catch (error) {
      toast.error("Kunde inte rensa: " + (error.response?.data?.detail || error.message));
    } finally {
      setClearing(false);
    }
  };

  const toggleColumn = (columnId) => {
    setSelectedColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const selectAllColumns = () => {
    setSelectedColumns(EXPORT_COLUMNS.map(c => c.id));
  };

  const selectNoneColumns = () => {
    setSelectedColumns([]);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const detailsItem = showDetailsDialog ? transferHistory.find(h => h.id === showDetailsDialog) : null;

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />

      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-6 w-6 text-primary" />
            <h1
              className="font-heading text-2xl font-bold uppercase tracking-tight"
              data-testid="import-export-title"
            >
              Import & Export
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          
          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Hantera lagerdata</CardTitle>
              <CardDescription>Importera eller exportera lagerdata via Excel/CSV-filer</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Import row */}
              <div>
                <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Upload className="h-4 w-4 text-green-500" />
                  Import
                </h3>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={() => setShowImportDialog(true)} 
                    className="gap-2" 
                    data-testid="import-btn"
                  >
                    <Upload className="h-4 w-4" />
                    Importera lager
                  </Button>
                  <Button 
                    variant="outline" 
                    className="gap-2"
                    onClick={() => window.open(`${API}/inventory/import-template`, '_blank')}
                  >
                    <Download className="h-4 w-4" />
                    Ladda ner importmall
                  </Button>
                </div>
              </div>

              {/* Export row */}
              <div className="pt-4 border-t border-border">
                <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Download className="h-4 w-4 text-blue-500" />
                  Export
                </h3>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    variant="outline" 
                    onClick={handleExportSiteSmart} 
                    disabled={exporting}
                    className="gap-2" 
                    data-testid="export-btn"
                  >
                    <Download className="h-4 w-4" />
                    {exporting ? "Exporterar..." : "Exportera lager (SiteSmart)"}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowCustomExportDialog(true)}
                    className="gap-2" 
                    data-testid="custom-export-btn"
                  >
                    <Settings2 className="h-4 w-4" />
                    Anpassad export
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* History */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Historik
                  </CardTitle>
                  <CardDescription>Senaste importer och exporter</CardDescription>
                </div>
                {transferHistory.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-destructive hover:text-destructive"
                    onClick={() => setShowClearDialog(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Rensa
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <p className="text-sm text-muted-foreground">Laddar...</p>
              ) : transferHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Ingen historik ännu</p>
                  <p className="text-sm">Importer och exporter kommer visas här</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {transferHistory.map((item, index) => (
                    <div 
                      key={item.id || index}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        {item.type === 'import' ? (
                          <div className="p-2 rounded-lg bg-green-500/10">
                            <Upload className="h-5 w-5 text-green-500" />
                          </div>
                        ) : (
                          <div className="p-2 rounded-lg bg-blue-500/10">
                            <Download className="h-5 w-5 text-blue-500" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {item.type === 'import' ? 'Import' : 'Export'}
                            </span>
                            <Badge variant="outline" className="font-mono text-xs">
                              {item.filename}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {item.type === 'import' ? (
                              <span>
                                <span className="text-green-500">{item.imported} nya</span>
                                {item.updated > 0 && <>, <span className="text-blue-500">{item.updated} uppdaterade</span></>}
                                {item.skipped > 0 && <>, <span className="text-amber-500">{item.skipped} överhoppade</span></>}
                              </span>
                            ) : (
                              <span>{item.products_exported} produkter exporterade</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          {formatDate(item.timestamp)}
                        </div>
                        {item.type === 'import' && (item.imported_items?.length > 0 || item.updated_items?.length > 0) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowDetailsDialog(item.id)}
                          >
                            Visa detaljer
                            <ChevronDown className="h-4 w-4 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => {
        if (!open) {
          setSelectedFile(null);
        }
        setShowImportDialog(open);
      }}>
        <DialogContent className="max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Importera lagerdata
            </DialogTitle>
            <DialogDescription>
              Ladda upp en Excel-fil (.xlsx) med produkter
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!selectedFile ? (
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  Dra och släpp en Excel-fil här, eller klicka för att välja
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button asChild size="lg">
                    <span>Välj fil</span>
                  </Button>
                </label>
              </div>
            ) : (
              <div className="border-2 border-primary/50 bg-primary/5 rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <FileSpreadsheet className="h-8 w-8 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedFile(null)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Byt fil
                  </Button>
                </div>
              </div>
            )}
            
            <div className="bg-muted/50 rounded-lg p-4 text-sm space-y-2">
              <p className="font-medium">Förväntade kolumner (rad 1 = rubrik):</p>
              <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                <span>A: Antal</span>
                <span>B: EAN *</span>
                <span>C: Artikelnummer</span>
                <span>D: Info/Beskrivning</span>
                <span>E: Lagerplats **</span>
                <span>F: Tillverkare</span>
                <span>G: Kategori</span>
                <span>H: Inkommet</span>
                <span>I: Bokningsinfo</span>
                <span>J: Antal bokade</span>
                <span>K: Inköpspris</span>
                <span>L: Säljpris</span>
              </div>
              <p className="text-xs text-muted-foreground">* = obligatoriskt fält</p>
              <p className="text-xs text-muted-foreground">** = obligatoriskt om Antal &gt; 0 (måste finnas i systemet)</p>
              <p className="text-xs text-muted-foreground">Inköps-/Säljpris är valfria och uppdateras per produkt (oavsett lagerplats)</p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelImport} disabled={importing}>
              Avbryt
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!selectedFile || importing}
              className="gap-2"
            >
              {importing ? (
                <>Importerar...</>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Importera
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Export Dialog */}
      <Dialog open={showCustomExportDialog} onOpenChange={setShowCustomExportDialog}>
        <DialogContent className="max-w-md" data-testid="custom-export-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />
              Anpassad export
            </DialogTitle>
            <DialogDescription>
              Välj exportläge och vilka kolumner som ska inkluderas
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            {/* Export Mode Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Exportläge</Label>
              <div className="grid grid-cols-1 gap-2">
                <div 
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    exportMode === "detailed" 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-muted-foreground'
                  }`}
                  onClick={() => setExportMode("detailed")}
                  data-testid="export-mode-detailed"
                >
                  <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    exportMode === "detailed" ? 'border-primary' : 'border-muted-foreground'
                  }`}>
                    {exportMode === "detailed" && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Detaljerat (per lagerplats)</p>
                    <p className="text-xs text-muted-foreground">En rad per produkt och lagerplats</p>
                  </div>
                </div>
                <div 
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    exportMode === "grouped" 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-muted-foreground'
                  }`}
                  onClick={() => setExportMode("grouped")}
                  data-testid="export-mode-grouped"
                >
                  <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    exportMode === "grouped" ? 'border-primary' : 'border-muted-foreground'
                  }`}>
                    {exportMode === "grouped" && <div className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Grupperat (per produkt)</p>
                    <p className="text-xs text-muted-foreground">En rad per produkt, lagerplatser kommaseparerade</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Column Selection */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-sm font-medium">Kolumner</Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={selectAllColumns} className="h-7 text-xs">
                    Välj alla
                  </Button>
                  <Button variant="ghost" size="sm" onClick={selectNoneColumns} className="h-7 text-xs">
                    Avmarkera
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {EXPORT_COLUMNS.map((column) => (
                  <div 
                    key={column.id}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                      selectedColumns.includes(column.id) 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    onClick={() => toggleColumn(column.id)}
                  >
                    <Checkbox 
                      id={column.id}
                      checked={selectedColumns.includes(column.id)}
                      onCheckedChange={() => toggleColumn(column.id)}
                    />
                    <Label 
                      htmlFor={column.id} 
                      className="cursor-pointer flex-1 text-sm"
                    >
                      {column.label}
                    </Label>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-muted-foreground">
                {selectedColumns.length} av {EXPORT_COLUMNS.length} kolumner valda
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCustomExportDialog(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleCustomExport} 
              disabled={selectedColumns.length === 0 || exporting}
              className="gap-2"
            >
              {exporting ? (
                <>Exporterar...</>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Exportera
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={!!showDetailsDialog} onOpenChange={() => setShowDetailsDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Upload className="h-5 w-5 text-green-500" />
              Importdetaljer
            </DialogTitle>
            <DialogDescription>
              {detailsItem?.filename} - {formatDate(detailsItem?.timestamp || '')}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-4 rounded-lg bg-green-500/10 text-center">
                  <p className="text-3xl font-bold text-green-500">{detailsItem?.imported || 0}</p>
                  <p className="text-sm text-muted-foreground">Nya produkter</p>
                </div>
                <div className="p-4 rounded-lg bg-blue-500/10 text-center">
                  <p className="text-3xl font-bold text-blue-500">{detailsItem?.updated || 0}</p>
                  <p className="text-sm text-muted-foreground">Uppdaterade</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-500/10 text-center">
                  <p className="text-3xl font-bold text-amber-500">{detailsItem?.skipped || 0}</p>
                  <p className="text-sm text-muted-foreground">Överhoppade</p>
                </div>
              </div>

              {detailsItem?.imported_items?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Plus className="h-4 w-4 text-green-500" />
                    Nya produkter ({detailsItem.imported_items.length})
                  </h4>
                  <div className="space-y-1">
                    {detailsItem.imported_items.map((item, idx) => (
                      <div key={idx} className="text-sm p-3 bg-green-500/5 rounded-lg border border-green-500/20">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-mono text-xs text-muted-foreground">{item.ean}</span>
                            <span className="mx-2">·</span>
                            <span className="font-medium">{item.artikelnummer || '(ingen)'}</span>
                            {item.produktnamn && (
                              <span className="text-muted-foreground ml-2">{item.produktnamn}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{item.lagerplats}</Badge>
                            <Badge className="bg-green-500">{item.antal} st</Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailsItem?.updated_items?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 text-blue-500" />
                    Uppdaterade produkter ({detailsItem.updated_items.length})
                  </h4>
                  <div className="space-y-1">
                    {detailsItem.updated_items.map((item, idx) => (
                      <div key={idx} className="text-sm p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-mono text-xs text-muted-foreground">{item.ean}</span>
                            <span className="mx-2">·</span>
                            <span className="font-medium">{item.artikelnummer || '(ingen)'}</span>
                            {item.produktnamn && (
                              <span className="text-muted-foreground ml-2">{item.produktnamn}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{item.lagerplats}</Badge>
                            <div className="flex items-center gap-1 text-sm">
                              <span className="text-muted-foreground">{item.old_antal}</span>
                              <ArrowRight className="h-3 w-3" />
                              <span className={item.change > 0 ? "text-green-500 font-medium" : item.change < 0 ? "text-red-500 font-medium" : ""}>
                                {item.new_antal}
                              </span>
                              {item.change !== 0 && (
                                <Badge variant={item.change > 0 ? "default" : "destructive"} className="ml-1">
                                  {item.change > 0 ? '+' : ''}{item.change}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailsItem?.skipped_items?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Överhoppade rader ({detailsItem.skipped_items.length})
                  </h4>
                  <div className="space-y-1">
                    {detailsItem.skipped_items.map((item, idx) => (
                      <div key={idx} className="text-sm p-3 bg-amber-500/5 rounded-lg border border-amber-500/20">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Rad {item.row}</span>
                          <span className="text-amber-600">{item.reason}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(null)}>
              Stäng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear History Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Rensa historik?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="pt-4">
                <p>Detta kommer att permanent radera all import/export-historik.</p>
                <p className="text-destructive font-medium mt-2">Denna åtgärd kan inte ångras!</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowClearDialog(false)} disabled={clearing}>
              Avbryt
            </Button>
            <Button variant="destructive" onClick={handleClearHistory} disabled={clearing}>
              {clearing ? "Rensar..." : "Ja, rensa historik"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
